/**
 * Mensajes de validación del navegador en español. Los avisos nativos («Please fill out this
 * field») salen en el idioma del navegador, no de la página: se reemplazan en un solo lugar para
 * todos los formularios que usan `required`, `type="email"`, `min`, `max`, `minLength`…
 */
type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function messageFor(field: Field): string {
  const v = field.validity;
  if (v.valueMissing)
    return field instanceof HTMLSelectElement ? 'Elige una opción.' : 'Completa este campo.';
  if (v.typeMismatch)
    return field.type === 'email'
      ? 'Escribe un correo válido, p. ej. nombre@empresa.com.'
      : field.type === 'url'
        ? 'Escribe una dirección válida, p. ej. https://…'
        : 'El valor no tiene el formato esperado.';
  if (v.tooShort && 'minLength' in field) return `Escribe al menos ${field.minLength} caracteres.`;
  if (v.tooLong && 'maxLength' in field) return `Escribe máximo ${field.maxLength} caracteres.`;
  if (v.rangeUnderflow && 'min' in field) return `El valor mínimo es ${field.min}.`;
  if (v.rangeOverflow && 'max' in field) return `El valor máximo es ${field.max}.`;
  if (v.stepMismatch) return 'El valor no es válido para este campo.';
  if (v.badInput) return 'Escribe un número válido.';
  if (v.patternMismatch) return field.title || 'El valor no tiene el formato esperado.';
  return '';
}

function isField(target: EventTarget | null): target is Field {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

export function installSpanishValidity(): void {
  document.addEventListener(
    'invalid',
    (event) => {
      if (!isField(event.target)) return;
      event.target.setCustomValidity('');
      if (!event.target.validity.valid) event.target.setCustomValidity(messageFor(event.target));
    },
    true,
  );
  const reset = (event: Event) => {
    if (isField(event.target)) event.target.setCustomValidity('');
  };
  document.addEventListener('input', reset, true);
  document.addEventListener('change', reset, true);
}
